// src/frames/Boosts.tsx
import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    ResponsiveContainer,
} from 'recharts';
import { API_URL } from "../App";

interface Stats {
    totalRecords: number;
    currentBoosters: number;
    guilds: number;
}

interface BoostRecord {
    uniqueID: string;
    discordID: string;
    discordUsername: string;
    guildID: string;
    guildName: string;
    premiumStart: string | null;
    premiumSince: string | null;
    currentlyBoosting: boolean;
    boostCount: number;
    lastUpdated: string;
    entryCreated: string;
}

interface BoostStatPoint {
    timestamp: string;
    totalBoosts: number;
    totalUsersBoosting: number;
    guildId?: string;
    guildName?: string;
}

interface BoostsProps {
    stats: Stats | null;
    boosters: BoostRecord[];
}

type BoostsSection = 'general' | 'statistics' | 'boosters';

const Boosts: React.FC<BoostsProps> = ({ stats, boosters }) => {
    const [activeSection, setActiveSection] = useState<BoostsSection>('general');

    const [filterGuild, setFilterGuild] = useState('');
    const [filterUsername, setFilterUsername] = useState('');
    const [filterUserId, setFilterUserId] = useState('');
    const [filterCurrentlyBoosting, setFilterCurrentlyBoosting] = useState<'' | 'yes' | 'no'>('');
    type BoosterSortField =
        | 'discordUsername'
        | 'guildName'
        | 'boostCount'
        | 'premiumSince'
        | 'currentlyBoosting'
        | 'premiumStart';

    const [sortField, setSortField] = useState<BoosterSortField>('discordUsername');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [graphType, setGraphType] = useState<'all' | 'totalBoosts' | 'totalUsersBoosting'>('all');

    const [boostStats, setBoostStats] = useState<BoostStatPoint[]>([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState<string | null>(null);

    const handleBoosterSortClick = (field: BoosterSortField) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const boosterSortLabel = (field: BoosterSortField) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const filteredBoosters = useMemo(() => {
        const lowerGuild = filterGuild.toLowerCase();
        const lowerUser = filterUsername.toLowerCase();
        const userId = filterUserId.trim();

        const filtered = boosters.filter((b) => {
            if (lowerGuild && !b.guildName.toLowerCase().includes(lowerGuild)) return false;
            if (lowerUser && !b.discordUsername.toLowerCase().includes(lowerUser)) return false;
            if (userId && !b.discordID.includes(userId)) return false;

            if (filterCurrentlyBoosting === 'yes' && !b.currentlyBoosting) return false;
            if (filterCurrentlyBoosting === 'no' && b.currentlyBoosting) return false;

            return true;
        });

        const sorted = [...filtered].sort((a, b) => {
            let aVal: string | number | boolean | null = null;
            let bVal: string | number | boolean | null = null;

            if (sortField === 'discordUsername') {
                aVal = a.discordUsername.toLowerCase();
                bVal = b.discordUsername.toLowerCase();
            } else if (sortField === 'guildName') {
                aVal = a.guildName.toLowerCase();
                bVal = b.guildName.toLowerCase();
            } else if (sortField === 'boostCount') {
                aVal = a.boostCount;
                bVal = b.boostCount;
            } else if (sortField === 'premiumSince') {
                aVal = a.premiumSince ? new Date(a.premiumSince).getTime() : null;
                bVal = b.premiumSince ? new Date(b.premiumSince).getTime() : null;
            } else if (sortField === 'premiumStart') {
                aVal = a.premiumStart ? new Date(a.premiumStart).getTime() : null;
                bVal = b.premiumStart ? new Date(b.premiumStart).getTime() : null;
            } else if (sortField === 'currentlyBoosting') {
                aVal = a.currentlyBoosting;
                bVal = b.currentlyBoosting;
            }

            if (aVal === bVal) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            return sortDirection === 'asc' ? 1 : -1;
        });

        return sorted;
    }, [
        boosters,
        filterGuild,
        filterUsername,
        filterUserId,
        filterCurrentlyBoosting,
        sortField,
        sortDirection,
    ]);


    useEffect(() => {
        if (activeSection !== 'statistics') return;

        const fetchStats = async () => {
            try {
                setStatsLoading(true);
                setStatsError(null);

                const params: Record<string, string> = {};
                if (dateFrom) params.from = dateFrom;
                if (dateTo) params.to = dateTo;

                const response = await axios.get<BoostStatPoint[]>(
                    `${API_URL}/api/boosts/individual-stats`,
                    { params }
                );

                setBoostStats(response.data || []);
            } catch (err: any) {
                console.error('Failed to load boost statistics:', err);
                setStatsError('Failed to load statistics. Please try again later.');
            } finally {
                setStatsLoading(false);
            }
        };

        fetchStats();
    }, [activeSection, dateFrom, dateTo]);

    // Prepare Recharts data
    const chartData = useMemo(() => {
        return boostStats.map((point) => {
            const d = new Date(point.timestamp);
            const label = isNaN(d.getTime())
                ? point.timestamp
                : d.toLocaleString('en-GB');

            return {
                time: label,
                totalBoosts: point.totalBoosts,
                totalUsers: point.totalUsersBoosting,
            };
        });
    }, [boostStats]);

    return (
        <>
            {/* QUICK STATS */}
            <section className="section">
                <h1>Boosts Overview</h1>
                <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                    Quick statistics across your servers.
                </p>

                {stats ? (
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">📚</div>
                            <div className="stat-content">
                                <h3>Total Records</h3>
                                <div className="stat-number">{stats.totalRecords}</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🚀</div>
                            <div className="stat-content">
                                <h3>Current Boosters</h3>
                                <div className="stat-number">{stats.currentBoosters}</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🏰</div>
                            <div className="stat-content">
                                <h3>Servers</h3>
                                <div className="stat-number">{stats.guilds}</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="no-data">
                        No stats available yet. Once data is collected, you&apos;ll see it here.
                    </div>
                )}

                {/* SECONDARY NAV / TABS */}
                <div className="boosts-subnav">
                    <button
                        className={activeSection === 'general' ? 'boosts-subnav-btn active' : 'boosts-subnav-btn'}
                        onClick={() => setActiveSection('general')}
                    >
                        General
                    </button>
                    <button
                        className={activeSection === 'statistics' ? 'boosts-subnav-btn active' : 'boosts-subnav-btn'}
                        onClick={() => setActiveSection('statistics')}
                    >
                        Statistics
                    </button>
                    <button
                        className={activeSection === 'boosters' ? 'boosts-subnav-btn active' : 'boosts-subnav-btn'}
                        onClick={() => setActiveSection('boosters')}
                    >
                        Boosters
                    </button>
                </div>
            </section>

            {/* GENERAL SECTION */}
            {activeSection === 'general' && (
                <section className="section" style={{ marginTop: '1.5rem' }}>
                    <h3>General</h3>
                    <p style={{ opacity: 0.8 }}>
                        Nothing in this section yet, try navigating to one of the other tabs!
                    </p>
                </section>
            )}

            {/* STATISTICS SECTION */}
            {activeSection === 'statistics' && (
                <section className="section" style={{ marginTop: '1.5rem' }}>
                    <h3>Statistics</h3>
                    <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                        View the logged statistics in graph form!
                    </p>

                    {/* Filters */}
                    <div className="boosts-filters-row">
                        <div className="boosts-filter">
                            <label>Date from</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="boosts-filter">
                            <label>Date to</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>
                        <div className="boosts-filter">
                            <label>Graph data</label>
                            <select
                                value={graphType}
                                onChange={(e) =>
                                    setGraphType(e.target.value as 'all' | 'totalBoosts' | 'totalUsersBoosting')
                                }
                            >
                                <option value="all">All Data</option>
                                <option value="totalBoosts">Total Boosts</option>
                                <option value="totalUsersBoosting">Total Users Boosting</option>
                            </select>
                        </div>
                    </div>

                    {statsLoading && (
                        <div className="no-data" style={{ marginTop: '0.5rem' }}>
                            Loading statistics...
                        </div>
                    )}

                    {statsError && (
                        <div className="error-message" style={{ marginTop: '0.5rem' }}>
                            {statsError}
                        </div>
                    )}

                    {/* Recharts chart */}
                    <div className="boosts-graph">
                        {!statsLoading && !statsError && chartData.length === 0 && (
                            <div className="no-data">
                                No data available for the selected filters.
                            </div>
                        )}

                        {!statsLoading && !statsError && chartData.length > 0 && (
                            <div style={{ width: '100%', height: 340 }}>
                                <ResponsiveContainer>
                                    <Tooltip cursor={{ fill: '#0A1430' }} />
                                    {graphType === 'all' ? (
                                        <LineChart
                                            data={chartData}
                                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="time" />
                                            <YAxis allowDecimals={false} domain={['auto', 'auto']} />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="totalBoosts"
                                                name="Total Boosts"
                                                stroke="#8884d8"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="totalUsers"
                                                name="Total Users Boosting"
                                                stroke="#82ca9d"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        </LineChart>
                                    ) : (graphType === 'totalBoosts' ? (
                                        <LineChart
                                            data={chartData}
                                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="time" />
                                            <YAxis allowDecimals={false} domain={['auto', 'auto']} />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="totalBoosts"
                                                name="Total Boosts"
                                                stroke="#8884d8"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        </LineChart>
                                    ) : (graphType === 'totalUsersBoosting' ? (
                                        <LineChart
                                            data={chartData}
                                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="time" />
                                            <YAxis allowDecimals={false} domain={['auto', 'auto']} />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="totalUsers"
                                                name="Total Users Boosting"
                                                stroke="#82ca9d"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        </LineChart>
                                    ) : (
                                        <LineChart
                                            data={chartData}
                                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="time" />
                                            <YAxis allowDecimals={false} domain={['auto', 'auto']} />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="totalBoosts"
                                                name="Total Boosts"
                                                stroke="#8884d8"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="totalUsers"
                                                name="Total Users Boosting"
                                                stroke="#82ca9d"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        </LineChart>
                                    )))}
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* BOOSTERS SECTION */}
            {activeSection === 'boosters' && (
                <section className="section" style={{ marginTop: '1.5rem' }}>
                    <h3>Boosters</h3>
                    <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                        Check the information for all the boost records (every member, from every server they've boosted)!
                    </p>

                    {/* Filter controls */}
                    <div className="boosts-filters">
                        <div className="boosts-filter">
                            <label>Server name</label>
                            <input
                                type="text"
                                placeholder="Search server..."
                                value={filterGuild}
                                onChange={(e) => setFilterGuild(e.target.value)}
                            />
                        </div>
                        <div className="boosts-filter">
                            <label>Username</label>
                            <input
                                type="text"
                                placeholder="Search username..."
                                value={filterUsername}
                                onChange={(e) => setFilterUsername(e.target.value)}
                            />
                        </div>
                        <div className="boosts-filter">
                            <label>User ID</label>
                            <input
                                type="text"
                                placeholder="Search user ID..."
                                value={filterUserId}
                                onChange={(e) => setFilterUserId(e.target.value)}
                            />
                        </div>
                        <div className="boosts-filter">
                            <label>Currently boosting</label>
                            <select
                                value={filterCurrentlyBoosting}
                                onChange={(e) =>
                                    setFilterCurrentlyBoosting(e.target.value as '' | 'yes' | 'no')
                                }
                            >
                                <option value="">All</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    {filteredBoosters.length === 0 ? (
                        <div className="no-data">
                            No boosters match your current filters.
                        </div>
                    ) : (
                        <div className="schedule-table-wrapper">
                            <table className="schedule-table">
                                <thead>
                                    <tr>
                                        <th
                                            className="is-clickable mobile-hidden"
                                            onClick={() => handleBoosterSortClick('discordUsername')}
                                        >
                                            User{boosterSortLabel('discordUsername')}
                                        </th>
                                        <th
                                            className="is-clickable"
                                            onClick={() => handleBoosterSortClick('guildName')}
                                        >
                                            Server{boosterSortLabel('guildName')}
                                        </th>
                                        <th>User ID</th>
                                        <th
                                            className="is-clickable mobile-hidden"
                                            onClick={() => handleBoosterSortClick('boostCount')}
                                        >
                                            Boost Count{boosterSortLabel('boostCount')}
                                        </th>
                                        <th
                                            className="is-clickable mobile-hidden"
                                            onClick={() => handleBoosterSortClick('premiumSince')}
                                        >
                                            Boosting Since{boosterSortLabel('premiumSince')}
                                        </th>
                                        <th
                                            className="is-clickable mobile-hidden"
                                            onClick={() => handleBoosterSortClick('premiumStart')}
                                        >
                                            First Boosted{boosterSortLabel('premiumStart')}
                                        </th>
                                        <th
                                            className="is-clickable"
                                            onClick={() => handleBoosterSortClick('currentlyBoosting')}
                                        >
                                            Currently Boosting{boosterSortLabel('currentlyBoosting')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBoosters.map((booster) => (
                                        <tr key={booster.uniqueID} className="schedule-row">
                                            <td className="mobile-hidden">{booster.discordUsername}</td>
                                            <td>{booster.guildName}</td>
                                            <td>{booster.discordID}</td>
                                            <td className="mobile-hidden">{booster.boostCount}</td>
                                            <td className="mobile-hidden">
                                                {booster.premiumSince
                                                    ? new Date(
                                                        booster.premiumSince
                                                    ).toLocaleDateString('en-GB')
                                                    : 'N/A'}
                                            </td>
                                            <td className="mobile-hidden">
                                                {booster.premiumStart
                                                    ? new Date(
                                                        booster.premiumStart
                                                    ).toLocaleDateString('en-GB')
                                                    : 'N/A'}
                                            </td>
                                            <td>{booster.currentlyBoosting ? 'Yes' : 'No'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}
        </>
    );
};

export default Boosts;
